import { useNavigate } from "react-router-dom";
import {
  Plus,
  Calendar,
  Receipt,
  Users,
  User,
  AlertTriangle,
  Package,
  MessageSquare,
  Bell,
  Shield,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../ui/dropDown";
import { Button } from "../ui/button";
import { cn } from "../ui/utils";
import { useMediaQuery } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { useTour } from "../ui/TourContext";
import AuthContext from "../../hooks/authContext";

const addNewOptions = [
  {
    label: "Schedule",
    href: "/operations/appoinmentSchedule",
    icon: <Calendar size={16} />,
    category: "Operations",
    target: "appointment-create",
  },
  {
    label: "Invoice",
    href: "/invoices/create",
    icon: <Receipt size={16} />,
    category: "Operations",
  },
  {
    label: "Employee",
    href: "/peoples/createEmp",
    icon: <Users size={16} />,
    category: "People",
  },
  {
    label: "Client",
    href: "/peoples/createClient",
    icon: <User size={16} />,
    category: "People",
  },
  {
    label: "Issue",
    href: "/operations/issues/manage",
    icon: <AlertTriangle size={16} />,
    category: "Operations",
  },
  {
    label: "Supply",
    href: "/supplies/cleaning/review",
    icon: <Package size={16} />,
    category: "Operations",
  },
  {
    label: "Message Channel",
    href: "/communications/message-board/create-channel",
    icon: <MessageSquare size={16} />,
    category: "Communications",
  },
  {
    label: "Notification",
    href: "/communications/notifications/create",
    icon: <Bell size={16} />,
    category: "Communications",
  },
  {
    label: "Admin",
    href: "/account/userAdmin",
    icon: <Shield size={16} />,
    category: "Account",
  },
];

const groupedOptions = addNewOptions.reduce((acc, option) => {
  if (!acc[option.category]) {
    acc[option.category] = [];
  }
  acc[option.category]?.push(option);
  return acc;
}, {});

export const AddNewButton = ({ collapsed = false, handleDrawerToggle }) => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const navigate = useNavigate();
  const { currentStep } = useTour();
  const [menuOpen, setMenuOpen] = useState(false);
  const openedRef = useRef(false);
  const auth = useContext(AuthContext);

  const handleOptionClick = (href) => {
    navigate(href);
  };

  const startTours = localStorage.getItem("startTour");

  useEffect(() => {
    if (
      startTours === "appointments" &&
      currentStep === 2 &&
      !openedRef.current
    ) {
      setMenuOpen(true);
      openedRef.current = true;
    }
  }, [startTours, currentStep]);

  let filteredOptions = groupedOptions;
  const userRole = auth?.userProfile?.role;
  const empRole = auth?.userProfile?.employeerole;

  const roles = Array.isArray(userRole)
    ? [...userRole]
    : userRole
    ? [userRole]
    : [];

  if (roles.includes("Employee") && empRole) {
    roles.push(empRole);
  }

  const allowedCategories = new Set();

  if (roles.includes("admin") && roles.includes("Employee") && empRole === "Admin") {
    Object.keys(groupedOptions).forEach(cat => allowedCategories.add(cat));
  }

  else if (roles.includes("admin")) {
    ["People", "Account"].forEach(cat => allowedCategories.add(cat));
  }

  else if (roles.includes("Employee") && empRole === "Admin") {
    Object.keys(groupedOptions).forEach(cat => {
      if (cat !== "Account") allowedCategories.add(cat);
    });
  }

  else {
    Object.keys(groupedOptions).forEach(cat => allowedCategories.add(cat));
  }

  filteredOptions = Object.fromEntries(
    Object.entries(groupedOptions).filter(([category]) =>
      allowedCategories.has(category)
    )
  );

  return (
    <div className="px-3 py-2">
        <DropdownMenu
          open={menuOpen}
          onOpenChange={(open) => {
            if (open && isMobile) {
              handleDrawerToggle();
              setMenuOpen(!menuOpen);
            }
            setMenuOpen(!menuOpen);
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button
              variant="default"
              className={cn(
                "w-full justify-center bg-primary hover:bg-primary/90 text-primary-foreground font-medium transition-all duration-200 d-flex flex-center",
                collapsed ? "px-2" : "px-4 py-2"
              )}
            >
              <Plus
                size={18}
                className={cn("shrink-0", !collapsed && "mr-2")}
              />
              {!collapsed && <span>Add New</span>}
            </Button>
          </DropdownMenuTrigger>
          {menuOpen && (
            <DropdownMenuContent
              className="w-80 addNewButton"
              align="start"
              side="right"
              sideOffset={8}
            >
              {Object.entries(filteredOptions)?.map(
                ([category, options], categoryIndex) => (
                  <div key={category}>
                    {categoryIndex > 0 && <DropdownMenuSeparator />}
                    <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {category}
                    </DropdownMenuLabel>
                    {options?.map((option) => (
                      <DropdownMenuItem
                        key={option.label}
                        onClick={() => handleOptionClick(option.href)}
                        {...(option?.target && { "data-tour": option?.target })}
                        className={cn(
                          "cursor-pointer flex items-center gap-3 px-3 py-2 text-sm"
                        )}
                      >
                        {option?.icon}
                        <span>{option?.label}</span>
                      </DropdownMenuItem>
                    ))}
                  </div>
                )
              )}
            </DropdownMenuContent>
          )}
        </DropdownMenu>
    </div>
  );
};
